import React, { useEffect, useRef, useState } from "react";
import { Tabs, Input, Button, Upload, Image, Row, Col, Space, message, Checkbox } from "antd";
import { UploadOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { GlobalButton } from "../../../comman/GlobalButton.styles";
import SignatureCanvas from "react-signature-canvas";
import "@fontsource/pacifico";
import "@fontsource/dancing-script";
import "@fontsource/allura";
import "@fontsource/great-vibes";

const WIDTH = 300;
const HEIGHT = 120;
const FONTS = ["Pacifico", "Dancing Script", "Allura", "Great Vibes"];

interface Props {
	onSave: (file: File) => void;
	onClear?: () => void;
	initialSignatureUrl?: string;
	hideDraw?: boolean;
	defaultTypedName?: string;
}

const DigitalSignature: React.FC<Props> = ({ onSave, onClear, initialSignatureUrl, hideDraw, defaultTypedName }) => {
	const sigRef = useRef<any>(null);

	const [mode, setMode] = useState<"draw" | "type" | "upload">("draw");
	const [typedName, setTypedName] = useState(defaultTypedName || "");
	const [font, setFont] = useState(FONTS[0]);
	const [preview, setPreview] = useState<string | null>(null);
	const [authorized, setAuthorized] = useState(false);
	const [isPreloaded, setIsPreloaded] = useState(false);

	// ---------------- LOAD EXISTING SIGNATURE ----------------
	useEffect(() => {
		if (!initialSignatureUrl) return;

		setPreview(initialSignatureUrl);
		setIsPreloaded(true);
		setAuthorized(true); // already approved earlier
	}, [initialSignatureUrl]);

	useEffect(() => {
		if (defaultTypedName) {
			setTypedName(defaultTypedName);
			setMode("type");
		}
	}, [defaultTypedName]);

	// ---------------- UTILS ----------------
	const clearAll = () => {
		sigRef.current?.clear();
		setPreview(null);
		setTypedName("");
		setAuthorized(false);
		setIsPreloaded(false);
		onClear?.();
	};

	const ensureAuthorized = () => {
		if (!authorized) {
			message.error("You must authorize Instarails to use this digital signature");
			return false;
		}
		return true;
	};

	const canvasToFile = (canvas: HTMLCanvasElement) => {
		const dataUrl = canvas.toDataURL("image/png");
		fetch(dataUrl)
			.then((r) => r.blob())
			.then((blob) => {
				const file = new File([blob], "digital-signature.png", {
					type: "image/png"
				});
				setPreview(dataUrl);
				onSave(file);
			});
	};

	// ---------------- SAVE HANDLERS ----------------
	const saveDraw = () => {
		if (!sigRef.current || sigRef.current.isEmpty()) {
			message.error("Draw your signature first");
			return;
		}
		if (!ensureAuthorized()) return;

		const src = sigRef.current.getCanvas();
		const canvas = document.createElement("canvas");
		canvas.width = WIDTH;
		canvas.height = HEIGHT;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		ctx.clearRect(0, 0, WIDTH, HEIGHT);
		ctx.drawImage(src, 0, 0, WIDTH, HEIGHT);

		canvasToFile(canvas);
	};

	const saveTyped = () => {
		if (!ensureAuthorized()) return;

		if (!typedName.trim()) {
			message.error("Enter your name");
			return;
		}

		const canvas = document.createElement("canvas");
		canvas.width = WIDTH;
		canvas.height = HEIGHT;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		let size = 48;
		ctx.font = `${size}px ${font}`;
		while (ctx.measureText(typedName).width > WIDTH - 20) {
			size -= 2;
			ctx.font = `${size}px ${font}`;
		}

		ctx.fillStyle = "#000";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(typedName, WIDTH / 2, HEIGHT / 2);

		canvasToFile(canvas);
	};

	// ---------------- UPLOAD ----------------
	const uploadProps = {
		beforeUpload: (file: File) => {
			if (!ensureAuthorized()) return Upload.LIST_IGNORE;

			const valid = ["image/png", "image/jpeg", "image/jpg"].includes(file.type);
			if (!valid) {
				message.error("Only PNG / JPG allowed");
				return Upload.LIST_IGNORE;
			}

			setPreview(URL.createObjectURL(file));
			onSave(file);
			message.success("Signature saved");

			return false;
		},
		maxCount: 1
	};

	const showAuthorization = !isPreloaded && !preview;
	const tabs = [!hideDraw && { key: "draw", label: "Draw" }, { key: "type", label: "Type" }].filter(Boolean);
	// ---------------- UI ----------------
	return (
		<div style={{ maxWidth: 520 }}>
			<Tabs
				activeKey={mode}
				onChange={(k) => {
					clearAll();
					setMode(k as any);
				}}
				items={tabs as any}
			/>

			{mode === "draw" && !preview && (
				<SignatureCanvas
					ref={sigRef}
					canvasProps={{
						width: WIDTH,
						height: HEIGHT,
						style: { border: "1px dashed #aaa", borderRadius: 8 }
					}}
				/>
			)}

			{mode === "type" && !preview && (
				<>
					<Input placeholder="Type your name" value={typedName} onChange={(e) => setTypedName(e.target.value)} />
					<Row gutter={[12, 12]} style={{ marginTop: 12 }}>
						{FONTS.map((f) => (
							<Col span={12} key={f}>
								<div
									onClick={() => setFont(f)}
									style={{
										fontFamily: f,
										fontSize: 24,
										padding: 8,
										textAlign: "center",
										border: font === f ? "2px solid #1677ff" : "1px solid #ddd",
										borderRadius: 6,
										cursor: "pointer"
									}}
								>
									{typedName || "Your Name"}
								</div>
							</Col>
						))}
					</Row>
				</>
			)}

			{mode === "upload" && !preview && (
				<Upload {...uploadProps}>
					<Button icon={<UploadOutlined />}>Upload signature</Button>
				</Upload>
			)}

			{preview && (
				<div>
					<Image
						src={preview}
						width={WIDTH}
						style={{
							marginTop: 16,
							border: "1px solid #ddd",
							borderRadius: 8
						}}
					/>
				</div>
			)}

			{showAuthorization && (
				<Checkbox checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} style={{ marginTop: 16 }}>
					I authorize Instarails to use the digital signature just created on all invoices
				</Checkbox>
			)}

			<Space style={{ marginTop: 16 }}>
				<GlobalButton type="default" icon={<DeleteOutlined />} onClick={clearAll}>
					Clear
				</GlobalButton>
				{!preview && (
					<GlobalButton
						className="primary"
						type="primary"
						icon={<SaveOutlined />}
						onClick={mode === "draw" ? saveDraw : saveTyped}
						disabled={mode === "upload"}
					>
						Save
					</GlobalButton>
				)}
			</Space>
		</div>
	);
};

export default DigitalSignature;
